import React, {useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {message} from "antd";
import Chat, {NoMessagesIcon} from "../Success/Chat";
import {ChatHeader} from "../Success/Chat/style";
import Call from "../Success/Call";
import {ChatSDK} from "@tencentcloud/chat";
import produce, {current} from "immer";
import {createChat} from "../Success/Chat/tencent";
import {initTencentCall, sentCallStateMessage} from "../Success/Call/tencent";
import {
    addCallUser,
    delCallUser,
    getChatUserToken, getThreadDetail,
    postChatAnonymousStart,
    sleep
} from "../../services/app";
import T from "../../services/language";
import {ButtonSubmit} from "../../components/Button";
import {useApp} from "../../contexts/app";

type AnonymousChatPopupProps = {
    style?: React.CSSProperties;
    fullButton?: boolean;
    fixed?: boolean;
};

const LOCAL_STORAGE_THREAD_KEY = 'anonymous_chat_thread';

export const cleanAnonymousChatThread = () => {
    sessionStorage.removeItem(LOCAL_STORAGE_THREAD_KEY);
    // try destroy chat and call engine
    try {
        if (!!globalThread?.chatSDK) {
            globalThread?.chatSDK?.destroy?.();
        }
        if (!!globalThread?.callEngine) {
            globalThread?.callEngine?.destroy?.();
        }
    } catch (e) {
    }

    globalThread = null;
    globalCallInvited = null;
    globalCalling = null;
    globalCallRef = null;
}

let globalThread: any = null;
let globalCallInvited: any = null;
let globalCalling: any = null;
let globalCallRef: any = null;

export default function AnonymousChatPopup({style, fullButton = false, fixed = true}: AnonymousChatPopupProps) {
    const [thread, setThread] = useState<{
        chatSDK?: ChatSDK;
        callEngine?: any;
        tencentUserId?: string;
        tencentGroupId?: string;
        endChatAt?: string;
        name?: string;
        threadId?: string;
        hidden?: boolean;
        calling?: boolean;
        callInfo?: any;
    } | null>(null);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState<'history' | 'chat' | 'call'>('chat');
    const callRef = useRef<any>(globalCallRef || null);
    const [callInvited, setCallInvited] = useState<any>(false);
    const [calling, setCalling] = useState(false);
    const timerAutoEndCallRef = useRef<any>({});
    const {campaign} = useApp();

    useEffect(() => {
        // load from global
        if (!!globalThread) {
            setThread(globalThread);
        }
        if (!!globalCallInvited) {
            setCallInvited(globalCallInvited);
        }
        if (!!globalCalling) {
            setCalling(globalCalling);
        }
    }, []);

    useEffect(() => {
        if (thread) {
            // sync to global
            globalThread = thread;
        }
        if (callInvited) {
            globalCallInvited = callInvited;
        }
        if (calling) {
            globalCalling = calling;
        }
        if (callRef?.current) {
            globalCallRef = callRef.current;
        }
    }, [thread, callRef?.current, calling, callInvited]);


    useEffect(() => () => {
        // cleanup timer
        if (timerAutoEndCallRef?.current?.timer) {
            clearTimeout(timerAutoEndCallRef.current.timer);
        }
    }, []);

    // Helper functions
    const getStoredThread = () => {
        const stored = sessionStorage.getItem(LOCAL_STORAGE_THREAD_KEY);
        return stored ? JSON.parse(stored) : null;
    };

    const saveThreadToStorage = (data, status) => {
        sessionStorage.setItem(LOCAL_STORAGE_THREAD_KEY, JSON.stringify({data, status}));
    };

    const clearThreadFromStorage = () => {
        sessionStorage.removeItem(LOCAL_STORAGE_THREAD_KEY);
    };

    const extractThreadConfig = (data) => ({
        tencentGroupId: data?.tencentGroupId,
        tencentUserId: data?.tencentCustomerUserId,
        id: data?.id,
        endChatAt: data?.endChatAt,
        name: data?.name,
        threadId: data?.threadId,
    });

    const validateThread = async (storedThread) => {
        if (!storedThread?.data?.data?.threadId) {
            return null;
        }

        try {
            const result = await getThreadDetail(storedThread.data.data.threadId);

            if (result?.status !== 200) {
                return null;
            }

            const threadData = result?.data?.data || {};
            console.log("Refresh thread detail:", threadData);

            // Check if thread is still active
            if (threadData.endedAt || !threadData.threadId) {
                return null;
            }

            return threadData;
        } catch (error) {
            console.error("Error validating thread:", error);
            return null;
        }
    };

    const loadOrCreateThread = async () => {
        // Try to get existing thread from storage
        const storedThread = getStoredThread();

        if (storedThread) {
            const validThread = await validateThread(storedThread);
            console.log("Validate stored thread:", validThread);
            if (validThread) {
                // Update storage with refreshed data
                saveThreadToStorage({data: validThread}, 200);
                console.log("Load chat thread from session storage", validThread);
                return {status: 200, data: {data: validThread}};
            }

            // Clear invalid thread from storage
            clearThreadFromStorage();
        }

        // Create new thread
        try {
            console.log("Session thread", globalThread);
            if (!!globalThread?.chatSDK) {
                try {
                    globalThread?.chatSDK?.destroy?.();
                    globalThread?.callEngine?.destroy?.();
                    console.log("Destroy old chat and call engine");
                } catch (e) {
                    console.log(e);
                }
            }
            console.log("Create new chat thread for campaign", campaign);
            const newThread = await postChatAnonymousStart({
                campaignId: campaign?.id
            });
            await sleep(500);
            console.log("Create new chat thread", newThread);
            return newThread;
        } catch (error) {
            console.error("Error creating new thread:", error);
            return null;
        }
    };

    const getUserToken = async (tencentUserId) => {
        try {
            const result = await getChatUserToken("", tencentUserId);
            return result?.status === 200 ? result : null;
        } catch (error) {
            console.error("Error getting user token:", error);
            return null;
        }
    };

    const initializeChatServices = async (config) => {
        const chatConfig = {
            appId: parseInt(config.appId),
            userId: config.tencentUserId,
            userSig: config.token,
        };

        try {
            const [chatSDK, callEngine] = await Promise.all([
                createChat(chatConfig),
                initTencentCall(chatConfig)
            ]);

            return {chatSDK, callEngine};
        } catch (error) {
            console.error("Error initializing chat services:", error);
            return {chatSDK: null, callEngine: null};
        }
    };

    const onStart = async () => {
        setLoading(true);
        try {
            // clean other chat
            try {
                // @ts-ignore
                window?.chatSDK?.destroy?.();
                // @ts-ignore
                window?.callEngine?.destroy?.();
                console.log("Destroy old window chat and call engine");
            } catch (e) {
            }

            // Step 1: Load existing thread or create new one
            let threadResult = await loadOrCreateThread();

            if (threadResult?.status !== 200) {
                throw new Error(T("Cannot start chat!"));
            }

            // Save thread to storage
            saveThreadToStorage(threadResult.data, threadResult.status);

            // Step 2: Extract configuration from thread
            let config: any = extractThreadConfig(threadResult.data.data);

            // Step 3: Get user token
            let tokenResult = await getUserToken(config.tencentUserId);

            // If token retrieval fails, try creating a new thread
            if (!tokenResult) {
                console.log("Get user token failed, try start new thread");
                threadResult = await postChatAnonymousStart({
                    campaignId: campaign?.id
                }).catch(() => null);

                if (threadResult?.status !== 200) {
                    throw new Error(T("Cannot access chat service!"));
                }

                // Update storage and config with new thread
                saveThreadToStorage(threadResult.data, threadResult.status);
                config = extractThreadConfig(threadResult.data.data);

                // Try getting token again
                tokenResult = await getUserToken(config.tencentUserId);

                if (!tokenResult) {
                    throw new Error(T("Cannot access chat service!"));
                }
            }

            // Step 4: Add token information to config
            config.appId = tokenResult.data.data.tencentSdkAppId;
            config.token = tokenResult.data.data.userSig;

            console.log("configLocal:", config, tokenResult);

            // Step 5: Initialize chat and call services
            const {chatSDK, callEngine} = await initializeChatServices(config);

            if (!chatSDK) {
                throw new Error(T("Cannot access chat service!"));
            }

            // Step 6: Update thread state
            setThread({
                chatSDK,
                callEngine,
                tencentUserId: config.tencentUserId,
                tencentGroupId: config.tencentGroupId,
                endChatAt: config.endChatAt,
                name: config.name,
                threadId: config.threadId,
                hidden: false,
            });

        } catch (error) {
            console.error("Error in onStart:", error);
            message.error(error.message || T("Cannot start chat!"));
        } finally {
            setLoading(false);
        }
    };

    // not enable live chat when campaign is off
    if (!campaign?.isOnLiveChat) {
        return null;
    }

    return (
        <>
            {!!thread ? (
                <div style={{
                    position: 'fixed',
                    width: "100%",
                    height: '100%',
                    zIndex: 999,
                    display: !!thread?.hidden ? "none" : "block"
                }}>
                    <Chat
                        isEndedChat={!!thread?.endChatAt}
                        bookingId={""}
                        tencentGroupId={thread.tencentGroupId as string}
                        mode={'customer'}
                        tencentUserId={thread.tencentUserId}
                        chatSDK={thread.chatSDK}
                        threadId={thread.threadId}
                        name={thread?.name}
                        callInvited={callInvited}
                        onAcceptInviteCall={() => {
                            callRef.current?.onAcceptInviteCall?.({
                                ...thread?.callInfo,
                                groupID: thread?.tencentGroupId,
                            });
                        }}
                        header={(<ChatHeader
                            view={view}
                            title={T("Live Chat")}
                            callType={thread?.callInfo?.callType}
                            setView={setView}
                            calling={!!calling}
                            onMinus={() => {
                                setThread(p => ({...(p as any), hidden: true}))
                            }}
                            name={(thread?.name || "")}
                        />)}
                        onMessage={(eventName: any, beMessage) => {
                            if (eventName === "MATCH-BOOKING") {
                                // Update thread name
                                console.log("MATCH-BOOKING message received:", beMessage);

                                setThread(produce((draft: any) => {
                                    // Update thread
                                    draft.threadId = beMessage?.threadId || draft.threadId;
                                    draft.name = beMessage?.name || draft.name;
                                    draft.tencentGroupId = beMessage?.tencentGroupId || draft.tencentGroupId;
                                    draft.tencentUserId = beMessage?.tencentUserId || draft.tencentUserId;
                                    // Update ThreadToStorage
                                    const currentStored = getStoredThread();
                                    if (currentStored?.data?.data) {
                                        currentStored.data.data.threadId = draft.threadId;
                                        currentStored.data.data.name = draft.name;
                                        currentStored.data.data.tencentGroupId = draft.tencentGroupId;
                                        currentStored.data.data.tencentUserId = draft.tencentUserId;
                                        saveThreadToStorage(currentStored.data, currentStored.status);
                                    }
                                    console.log("Updated thread after MATCH-BOOKING:", current(draft));
                                }))
                            }
                            if (eventName === 'END-CHAT' && thread?.tencentGroupId === beMessage?.tencentGroupId) {
                                try {
                                    thread?.chatSDK?.destroy?.();
                                    thread?.callEngine?.destroy?.();
                                    globalThread?.chatSDK?.destroy?.();
                                    globalThread?.callEngine?.destroy?.();
                                    console.log("Destroy old chat and call engine");
                                } catch (e) {
                                }
                                globalThread = null;
                                setThread(null);
                                message.destroy();
                            }
                            if (eventName === 'MESSAGE') {
                                // setThread(produce((draft: any) => {
                                //     const currentThead = current(draft);
                                //     if (!!currentThead?.hidden && currentThead?.tencentGroupId === beMessage?.tencentGroupId) {
                                //         draft.hidden = false;
                                //     }
                                // }))
                            }
                        }}
                    />

                    <Call
                        view={view}
                        user={{
                            fullName: T("Customer"),
                            avatar: "",
                            userName: thread.tencentUserId as string,
                        }}
                        ref={callRef}
                        callEngine={thread?.callEngine}
                        onInvite={(event) => {
                            // only accept invites for this group
                            if (event?.groupID === thread.tencentGroupId) {
                                setCallInvited(event);
                            } else {
                                callRef?.current?.endCall();
                            }
                        }}
                        onUserLeave={(userID) => {
                            setThread(produce((draft: any) => {
                                const currentThead = current(draft);
                                delCallUser(currentThead?.tencentGroupId, userID).finally(() => {
                                });
                            }))
                        }}
                        onStartCall={() => {
                            setView('call');
                            addCallUser(thread?.tencentGroupId as string, thread?.tencentUserId as string).finally(() => {
                                sentCallStateMessage(thread?.chatSDK, thread?.tencentGroupId as string, {
                                    tencentUserId: thread?.tencentUserId,
                                    bookingId: "",
                                    tencentGroupId: thread?.tencentGroupId,
                                    threadId: thread?.threadId,
                                    name: thread?.name || "",
                                    isCalling: false,
                                }, "CALL-JOIN-STATE").then()
                            });
                        }}
                        onEndCall={() => {
                            setCallInvited(false);
                            setThread(produce((draft: any) => {
                                const currentThead = current(draft);
                                delCallUser(currentThead?.tencentGroupId, currentThead?.tencentUserId).finally(() => {
                                    sentCallStateMessage(currentThead?.chatSDK, currentThead?.tencentGroupId, {
                                        tencentUserId: currentThead?.tencentUserId,
                                        bookingId: "",
                                        tencentGroupId: currentThead?.tencentGroupId,
                                        threadId: currentThead?.threadId,
                                        name: currentThead?.name || "",
                                        isCalling: false,
                                    }, "CALL-LEAVE-STATE").then()
                                });
                            }))
                            if (timerAutoEndCallRef?.current?.timer) {
                                clearTimeout(timerAutoEndCallRef.current.timer);
                            }
                            timerAutoEndCallRef.current = {timer: null, roomID: null};
                        }}
                        onChange={(isCalling, callInfo) => {
                            if (!isCalling && !!callInvited) {
                                setCallInvited(false);
                            }
                            setThread(produce((draft: any) => {
                                draft.calling = isCalling;
                                draft.callInfo = callInfo;
                            }))
                            setCalling(isCalling);
                        }}
                        mode={'customer'}
                    />
                </div>
            ) : null}

            {/* Floating button */}
            {thread ? (
                <FloatingButton
                    style={style}
                    $fullButton={fullButton}
                    $fixed={fixed}
                    loading={loading}
                    onClick={async () => {
                        setLoading(true);
                        // Get detail thread is
                        const result = await getThreadDetail(thread.threadId);
                        if (result?.status === 200) {
                            const data = result?.data?.data || {};
                            if (!data?.endedAt && data?.threadId) {
                                // still active
                                setThread(p => ({...(p as any), ...extractThreadConfig(data), hidden: false}));
                                setLoading(false);
                                return;
                            } else {
                                await onStart();
                                setLoading(false);
                                return;
                            }
                        } else {
                            await onStart();
                        }
                        setLoading(false);
                    }}
                >
                    {fullButton ? T("Chat with us") : null}
                    <Icon/>
                </FloatingButton>
            ) : (
                <FloatingButton
                    style={style}
                    $fullButton={fullButton}
                    $fixed={fixed}
                    onClick={onStart}
                    loading={loading}
                >
                    {fullButton ? T("Chat with us") : null}
                    <Icon/>
                </FloatingButton>
            )}
        </>
    );
}

const FloatingButton = styled(ButtonSubmit)<{ $fullButton: boolean; $fixed: boolean }>`
    height: ${(props) => (props.$fixed ? "56px" : "48px")};
    border-radius: 50px;
    background: #FFF !important;
    box-shadow: ${(props) => (props.$fixed ? "0px 0px 15px 0px rgba(0, 0, 0, 0.1)" : "none")};
    border: ${(props) => (props.$fixed ? "none" : `1px solid ${props.theme.main || "#1D896D"} !important`)};
    position: ${(props) => (props.$fixed ? "fixed !important" : "relative")};
    z-index: 1;
    bottom: ${(props) => {
        if (!props.$fixed) return "auto";
        return props.$fullButton ? "12px" : "78px";
    }};
    right: ${(props) => (props.$fixed ? "12px" : "auto")};
    width: ${(props) => {
        if (props.$fixed && !props.$fullButton) return "56px";
        if (!props.$fixed) return "48px";
        return "auto";
    }};
    min-width: ${(props) => {
        if (props.$fixed && !props.$fullButton) return "56px";
        if (!props.$fixed) return "48px";
        return "auto";
    }};
    padding: ${(props) => {
        if (!props.$fixed) return "0";
        return props.$fullButton ? "0 16px" : "0";
    }};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${(props => props.theme.main)} !important;
    flex-shrink: 0;

    svg {
        margin-left: ${(props) => (props.$fullButton ? "6px" : "0")};

        & * {
            fill: ${(props => props.theme.main)} !important;
        }
    }

    .ant-btn-loading-icon {
        margin-right: ${(props) => ((props.$fullButton) ? "6px" : "0")};
    }

    &.ant-btn > .ant-btn-loading-icon .anticon {
        padding: 0;
    }

    &.ant-btn-loading > svg {
        display: none;
    }
`;

const Icon = (props: any) => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M19.8333 2.33301H8.16665C4.94665 2.33301 2.33331 4.93467 2.33331 8.14301V15.1197V16.2863C2.33331 19.4947 4.94665 22.0963 8.16665 22.0963H9.91665C10.2316 22.0963 10.6516 22.3063 10.85 22.563L12.6 24.8847C13.37 25.9113 14.63 25.9113 15.4 24.8847L17.15 22.563C17.3716 22.2713 17.7216 22.0963 18.0833 22.0963H19.8333C23.0533 22.0963 25.6666 19.4947 25.6666 16.2863V8.14301C25.6666 4.93467 23.0533 2.33301 19.8333 2.33301ZM15.1666 16.0413H8.16665C7.68831 16.0413 7.29165 15.6447 7.29165 15.1663C7.29165 14.688 7.68831 14.2913 8.16665 14.2913H15.1666C15.645 14.2913 16.0416 14.688 16.0416 15.1663C16.0416 15.6447 15.645 16.0413 15.1666 16.0413ZM19.8333 10.208H8.16665C7.68831 10.208 7.29165 9.81134 7.29165 9.33301C7.29165 8.85467 7.68831 8.45801 8.16665 8.45801H19.8333C20.3116 8.45801 20.7083 8.85467 20.7083 9.33301C20.7083 9.81134 20.3116 10.208 19.8333 10.208Z"
            fill="#1D896D"/>
    </svg>
)
